import { createTheme } from "@mui/material";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0659F9",
      contrastText: "#fff",
      100: "#E5ECF9",
      200: "#AEC8F9",
      300: "#7DA8FA",
      400: "#4B86F9",
      500: "#0659F9",
      600: "#054CD6",
      700: "#03338F",
      800: "#021B4D",
    },
    gray: {
      contrastText: "#fff",
      100: "#FCFCFD",
      200: "#F2F4F7",
      300: "#D0D5DD",
      400: "#98A2B3",
      500: "#667085",
      600: "#475467",
      700: "#1B2730",
      800: "#10181E",
    },
    error: {
      main: "#D7343F",
      contrastText: "#fff",
      100: "#FEF3F2",
      200: "#FECDCA",
      300: "#FDA29B",
      400: "#F97066",
      500: "#D7343F",
      600: "#D92D20",
      700: "#B42318",
      800: "#912018",
    },
    warning: {
      main: "#FDB022",
      contrastText: "#fff",
      100: "#FFFCF5",
      200: "#FEF0C7",
      300: "#FEDF89",
      400: "#FEC84B",
      500: "#FDB022",
      600: "#F79009",
      700: "#DC6803",
      800: "#B54708",
    },
    success: {
      main: "#12B76A",
      contrastText: "#fff",
      100: "#ECFDF3",
      200: "#D1FADF",
      300: "#6CE9A6",
      400: "#32D583",
      500: "#12B76A",
      600: "#039855",
      700: "#027A48",
      800: "#05603A",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      xmd: 1120,
      lg: 1320,
      xl: 1440,
      xxl: 1536,
    },
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 1px 10px 0px #10182814",
          borderRadius: "16px",
          border: "1px solid #E3E6EA",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 99,
          fontSize: "1rem",
          lineHeight: 1.1,
          padding: "0.875rem 1.5rem",
          minWidth: "96px",
          boxShadow: "none",
          textTransform: "capitalize",

          "&:hover": {
            boxShadow: "0px 2px 4px rgb a(0, 0, 0, 0.25)",
          },

          "@media (max-width: 768px)": {
            fontSize: "1rem",
            padding: "0.75rem 1.25rem",
          },
        },
      },
      variants: [
        {
          props: { variant: "text" },

          style: {
            "&:hover": {
              color: "#0659F9",
              backgroundColor: "#E5ECF9",
            },
          },
        },
        {
          props: { variant: "text-secondary" },

          style: {
            color: "#1B2730",

            "&:hover": {
              color: "#1B2730",
              backgroundColor: "#F2F4F7",
            },
          },
        },
        {
          props: { variant: "outlined" },

          style: {
            border: "1px solid #0659F9",

            "&:hover": {
              color: "#0659F9",
              backgroundColor: "#E5ECF9",
            },
          },
        },
        {
          props: { variant: "outlined-secondary" },

          style: {
            color: "#344054",
            border: "1px solid #D0D5DD",

            "&:hover": {
              color: "#344054",
              backgroundColor: "#F2F4F7",
            },
          },
        },
        {
          props: { variant: "destructive" },
          style: {
            backgroundColor: "#F04438",

            "&:hover": {
              backgroundColor: "#D92D20",
            },
          },
        },
      ],
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: "20px",
          paddingRight: "20px",

          "@media (min-width: 600px)": {
            paddingLeft: "20px",
            paddingRight: "20px",
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "p",
          h2: "p",
          h3: "p",
          h4: "p",
          h5: "p",
          h6: "p",
          "body-2xl": "p",
          "body-xl": "p",
          "body-lg": "p",
          body: "p",
          "body-sm": "p",
          "body-xs": "p",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // "& > svg": {
          //   color: "#10181E",
          // },
        },
      },
    },
  },
  typography: {
    fontWeightRegular: 500,
    fontFamily: "HelveticaNowDisplay",

    h1: {
      letterSpacing: "-0.01em",
      lineHeight: 1.1,

      "@media (min-width: 0)": {
        fontSize: "2.5rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "3rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "3.5rem",
        lineHeight: "0.95",
      },
    },

    h2: {
      fontWeight: 900,
      lineHeight: 1.1,

      "@media (min-width: 0)": {
        fontSize: "2rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "2.5rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "3rem",
      },
    },

    h3: {
      fontWeight: 900,
      lineHeight: 1.2,

      "@media (min-width: 0)": {
        fontSize: "1.5rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "2rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "2.675rem",
      },
    },

    h4: {
      fontWeight: 900,
      lineHeight: 1.2,

      "@media (min-width: 0)": {
        fontSize: "1.25rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "1.75rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "2.25rem",
      },
    },

    h5: {
      "@media (min-width: 0)": {
        fontSize: "1.125rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "1.375rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "1.75rem",
      },
    },

    h6: {
      "@media (min-width: 0)": {
        fontSize: "1rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "1.125rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "1.25rem",
      },
    },

    "body-2xl": {
      "@media (min-width: 0)": {
        fontSize: "1.125rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "1.25rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "1.5rem",
      },
    },

    "body-xl": {
      "@media (min-width: 0)": {
        fontSize: "1rem",
      },

      "@media (min-width: 600px)": {
        fontSize: "1.125rem",
      },

      "@media (min-width: 768px)": {
        fontSize: "1.25rem",
      },
    },

    "body-lg": {
      fontSize: "1rem",

      "@media (min-width: 600px)": {
        fontSize: "1.125rem",
      },
    },

    body: {
      fontSize: "0.875rem",

      "@media (min-width: 600px)": {
        fontSize: "1rem",
      },
    },

    "body-sm": {
      fontSize: "0.875rem",
    },

    "body-xs": {
      fontSize: "0.75rem",
    },
  },
});

export default theme;
